import { ERROR_CODES } from '../models';

export type TurnstileAction =
  | 'login'
  | 'webauth-login'
  | 'share-provision-login'
  | 'share-provision-signup'
  | 'checkout';

/**
 * Backend error codes that signal a Cloudflare Turnstile (or related bot-protection)
 * rejection. These should be surfaced to the user via the ErrorMessages lookup instead
 * of being masked as 6001 "incorrect credentials".
 *  - 8998: token verification failed (missing/invalid/expired/already-used)
 *  - 8999: traffic blocked by upstream protection
 */
export const BOT_PROTECTION_ERROR_CODES = [
  ERROR_CODES.BOT_PROTECTION_BLOCKED_BY_CLOUDFLARE,
  ERROR_CODES.BOT_PROTECTION_BLOCKED_TRAFFIC,
] as const;

export function isBotProtectionCode(code: number | undefined): boolean {
  return code !== undefined && (BOT_PROTECTION_ERROR_CODES as readonly number[]).includes(code);
}

export interface TurnstileErrorData {
  turnstileErrorCode: string;
}

export type TurnstileTheme = 'auto' | 'light' | 'dark';

export type TurnstileSize = 'normal' | 'compact';

export type TurnstileRefreshExpired = 'auto' | 'manual' | 'never';

export interface TurnstileRenderOpts {
  sitekey: string;
  action?: string;
  theme?: TurnstileTheme;
  size?: TurnstileSize;
  'refresh-expired'?: TurnstileRefreshExpired;
  callback: (token: string) => void;
  'error-callback'?: (errorCode: string) => void;
  // Cloudflare fires this only when the visitor has to act, which is what lets a host reset a
  // widget out of sight and surface it only if it turns interactive.
  'before-interactive-callback'?: () => void;
  'expired-callback'?: (token: string) => void;
  'timeout-callback'?: () => void;
}

export interface TurnstileSdk {
  render(container: HTMLElement | string, opts: TurnstileRenderOpts): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
  getResponse(widgetId: string): string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileSdk;
  }
}
